import axios from 'axios';
import { useQuery, useQueryClient } from 'react-query';
import { NIL } from 'uuid';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 1402 — INTERNAL view of a sales order's customer-portal copy (the frozen snapshot the
// portal serves). The portal itself never reads a work order; this is the admin side that mints,
// re-shares (supersedes) or withdraws that copy from the sales order form.
export interface SalesOrderShareRow {
  id: string;
  number: string;
  title?: string | null;
  total: number;
  status: 'Sent' | 'Approved' | 'Declined' | 'Superseded' | 'Converted' | 'Complete' | 'Withdrawn';
  sentDate: string;
  approvedByName?: string | null;
  approvedDate?: string | null;
  declinedByName?: string | null;
  declinedDate?: string | null;
  declineReason?: string | null;
  invoiceId?: string | null;
  convertedDate?: string | null;
  completedDate?: string | null;
  withdrawnDate?: string | null;
  supersededDate?: string | null;
}

export interface SalesOrderShareState {
  active: SalesOrderShareRow | null;
  history: SalesOrderShareRow[];
  typeLocked: boolean; // the Document Type select locks once any copy was ever minted
}

export const salesOrderPortalKey = (id: string) => ['salesOrderPortal', id];

export const useSalesOrderPortal = (id?: string) => useQuery(
  salesOrderPortalKey(id ?? NIL),
  async () => {
    const { data } = await axios.get(`${apiUrl()}WorkOrder/${id}/portal-share`);
    return data as SalesOrderShareState;
  },
  { enabled: !!id && id !== NIL },
);

export const useSalesOrderPortalActions = (id: string) => {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries(salesOrderPortalKey(id));
  const share = async (): Promise<SalesOrderShareRow> => {
    const { data } = await axios.post(`${apiUrl()}WorkOrder/${id}/portal-share`);
    await refresh();
    return data as SalesOrderShareRow;
  };
  const withdraw = async (): Promise<number> => {
    const { data } = await axios.post(`${apiUrl()}WorkOrder/${id}/portal-withdraw`);
    await refresh();
    return data.withdrawn as number;
  };
  return { share, withdraw, refresh };
};
